<template>
  <div class="relative">
    <div class="gallery bg-gray-10">
      <Swiper
        :modules="modules"
        :slides-per-view="1"
        :navigation="true"
        :centered-slides="true"
        :loop="images.length > 1"
        :scrollbar="{
          hide: false,
        }"
        :thumbs="{
          swiper: thumbsSwiper,
        }"
        class="w-full"
      >
        <template #container-start>
          <div
            v-if="product.manufacturer?.media"
            class="absolute left-4 top-4 z-20 bg-white p-2"
          >
            <ShopSharedImage :media="product.manufacturer?.media" class="h-6" />
          </div>
        </template>
        <template #container-end>
          <div class="absolute right-4 top-4 z-20">
            <ClientOnly>
              <ShopProductWishlistHeart
                :product="product"
                class="rounded-full bg-white p-2"
              />
            </ClientOnly>
          </div>
        </template>
        <SwiperSlide v-for="image in images" :key="image.id" class="bg-gray-10">
          <ShopSharedImage
            :media="image"
            class="size-full object-cover mix-blend-multiply"
          />
        </SwiperSlide>
      </Swiper>
    </div>
    <div class="thumbs mt-4">
      <Swiper
        :modules="[SwiperThumbs]"
        :watch-slides-progress="true"
        :breakpoints="thumbsBreakpoints"
        :space-between="20"
        @swiper="setThumbsSwiper"
      >
        <SwiperSlide v-for="image in images" :key="image.id">
          <ShopSharedImage
            :media="image"
            class="size-full cursor-pointer object-cover"
          />
        </SwiperSlide>
      </Swiper>
    </div>
  </div>
</template>

<script setup lang="ts">
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import 'swiper/css/scrollbar'
import type { Swiper as SwiperType } from 'swiper'
import { breakpointsTailwind } from '@vueuse/core'
import type { Schemas } from '#shopware'

const props = defineProps<{
  product: Schemas['Product']
}>()

const modules = [
  SwiperNavigation,
  SwiperPagination,
  SwiperScrollbar,
  SwiperThumbs,
]

const thumbsSwiper = ref<SwiperType>()

const setThumbsSwiper = (swiper: SwiperType) => {
  thumbsSwiper.value = swiper
}

const thumbsBreakpoints = {
  0: {
    slidesPerView: 5,
  },
  [breakpointsTailwind.lg]: {
    slidesPerView: 6,
  },
  [breakpointsTailwind.xl]: {
    slidesPerView: 8,
  },
}

const images = computed<Schemas['Media'][]>(() => {
  const media = (props.product?.media || []).filter((media) => media.media)
  return media
    .filter((media) => media.media !== undefined)
    .map((media) => media.media as Schemas['Media'])
})
</script>

<style scoped lang="postcss">
.gallery {
  &::v-deep(.swiper-scrollbar) {
    @apply bottom-8 left-1/4 h-1 w-1/2 rounded-none bg-white lg:left-1/3 lg:w-1/3;
  }
  &::v-deep(.swiper-scrollbar-drag) {
    @apply rounded-none bg-black;
  }
  &::v-deep(.swiper-button-next),
  &::v-deep(.swiper-button-prev) {
    @apply size-8 bg-white text-black opacity-100;
    &::after {
      @apply text-sm font-bold;
    }
  }
  &::v-deep(.swiper-button-next) {
    @apply right-8 lg:right-4;
  }
  &::v-deep(.swiper-button-prev) {
    @apply left-8 lg:left-4;
  }
  &::v-deep(.swiper-slide) {
    @apply px-4 py-20 lg:px-20 xl:px-40;
  }
}
.thumbs {
  &::v-deep(.swiper-slide) {
    @apply border border-transparent bg-gray-10 p-2;
  }
  &::v-deep(.swiper-slide-thumb-active) {
    @apply border-black;
  }
}
</style>
